import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, TextField, MenuItem, Typography } from '@mui/material';
import dimensionService from '../services/dimension.service';
import { useLanguage } from '../i18n/LanguageContext';

interface Dimension {
  id: number; code: string; name: string; isRequired: boolean;
  values: { id: number; code: string; name: string }[];
}

interface DimAssignment { dimensionId: number; dimensionValueId: number; }

export interface DimensionSelectorRef {
  save: (entityType: string, entityId: number) => Promise<void>;
  getAssignments: () => DimAssignment[];
  loadFromEntity: (entityType: string, entityId: number) => Promise<void>;
}

interface Props {
  entityType: string;
  entityId?: number;            // If set, loads existing assignments
  defaultsFromType?: string;    // Load defaults from this entity type
  defaultsFromId?: number;      // Load defaults from this entity id
  disabled?: boolean;
  compact?: boolean;
}

const DimensionSelector = forwardRef<DimensionSelectorRef, Props>(
  ({ entityType, entityId, defaultsFromType, defaultsFromId, disabled, compact }, ref) => {
    const { t } = useLanguage();
    const [dimensions, setDimensions] = useState<Dimension[]>([]);
    const [selections, setSelections] = useState<Record<number, number>>({});

    useEffect(() => { loadDimensions(); }, []);

    useEffect(() => {
      if (entityId && entityId > 0) loadEntityDimensions(entityType, entityId);
      else if (defaultsFromType && defaultsFromId && defaultsFromId > 0)
        loadEntityDimensions(defaultsFromType, defaultsFromId);
    }, [entityId, defaultsFromType, defaultsFromId]);

    const loadDimensions = async () => {
      try {
        const res = await dimensionService.getAll();
        setDimensions(res.data.filter((d: any) => d.isActive));
      } catch (err: any) {
        console.warn('Financial dimensions not available:', err.message);
      }
    };

    const loadEntityDimensions = async (type: string, id: number) => {
      try {
        const res = await dimensionService.getDocumentDimensions(type, id);
        const sel: Record<number, number> = {};
        res.data.forEach((d: any) => { sel[d.dimensionId] = d.dimensionValueId; });
        setSelections(sel);
      } catch (err: any) {
        console.warn('Could not load entity dimensions:', err.message);
      }
    };

    const handleChange = (dimId: number, valueId: number) => {
      setSelections(prev => ({ ...prev, [dimId]: valueId }));
    };

    useImperativeHandle(ref, () => ({
      save: async (et: string, eid: number) => {
        const dims = Object.entries(selections)
          .filter(([, vid]) => vid > 0)
          .map(([did, vid]) => ({ dimensionId: Number(did), dimensionValueId: vid }));
        if (dims.length > 0) {
          await dimensionService.saveDocumentDimensions(et, eid, dims);
        }
      },
      getAssignments: () =>
        Object.entries(selections)
          .filter(([, vid]) => vid > 0)
          .map(([did, vid]) => ({ dimensionId: Number(did), dimensionValueId: vid })),
      loadFromEntity: async (type: string, id: number) => {
        await loadEntityDimensions(type, id);
      }
    }));

    if (dimensions.length === 0) return null;

    return (
      <Box sx={{ mt: compact ? 1 : 2, mb: compact ? 1 : 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          {t('dimensions.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {dimensions.map(dim => (
            <TextField
              key={dim.id} select size="small"
              label={dim.name + (dim.isRequired ? ' *' : '')}
              value={selections[dim.id] || ''}
              onChange={e => handleChange(dim.id, Number(e.target.value))}
              disabled={disabled}
              sx={{ minWidth: 180, flex: compact ? undefined : '1 1 200px' }}
            >
              <MenuItem value="">{t('common.none')}</MenuItem>
              {dim.values.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.code} — {v.name}</MenuItem>
              ))}
            </TextField>
          ))}
        </Box>
      </Box>
    );
  }
);

DimensionSelector.displayName = 'DimensionSelector';
export default DimensionSelector;
